import * as Sentry from '@sentry/browser'
import { takeEvery, call, put, select } from 'redux-saga/effects'
import queryString from 'query-string'
import { t } from 'ttag'

import { DEFAULT_JOBS_QUERY_PARAMS } from 'constants/projects'
import {
  LOG_IN,
  LOG_OUT,
  REQUEST_USER,
  RESET_PASSWORD,
  RESET_PASSWORD_CONFIRM,
  REGISTER_ACCOUNT,
  SET_LANGUAGE,
  SWITCH_ACCOUNT,
  getLanguage,
  resetPasswordFailed,
  resetPasswordConfirmFailed,
  loginFailed,
  requestUserSuccess,
  requestUserFailed,
  registerAccountSuccess,
  registerAccountFailed,
} from 'ducks/auth'
import { errorAction } from 'ducks/error'
import { toPasswordReset, toPasswordChangeDone, toProjects } from 'ducks/router'
import { initSnacksQueue } from 'ducks/ux'
import { parseQuery } from 'utils/query'
import * as auth from 'utils/auth'
import { stringify } from 'utils/json'

export default function* rootSaga() {
  yield takeEvery(LOG_OUT, logOutSaga)
  yield takeEvery(LOG_IN, logInSaga)
  yield takeEvery(REQUEST_USER, fetchUserSaga)
  yield takeEvery(RESET_PASSWORD, resetPasswordSaga)
  yield takeEvery(RESET_PASSWORD_CONFIRM, changePasswordSaga)
  yield takeEvery(REGISTER_ACCOUNT, registerAccountSaga)
  yield takeEvery(SET_LANGUAGE, changeUserLanguageSaga)
  yield takeEvery(SWITCH_ACCOUNT, changeUserAccountSaga)
}

const setUserContext = ({ pk, email, contributor_name }) => {
  // set user context for sentry error reporting.
  const user = { id: pk, name: contributor_name, email }
  if (process.env.NODE_ENV === 'production')
    Sentry.configureScope(scope => {
      scope.setUser(user)
    })
}

const redirectToNext = () => {
  // redirect browser to ?next=[url] if found in query
  // used with django @loginrequired views
  const query = parseQuery(global.location.search)
  if (query.next) {
    global.location.replace(query.next)
    return true
  }
  return false
}

function* logInSaga(action: any) {
  const { error } = yield call(auth.logIn, action.payload)
  if (error) {
    yield put(loginFailed(error))
  } else {
    // immediately redirect to next if found in query
    if (yield call(redirectToNext)) return
    // csrf token checked for each request
    yield put(toProjects(null, null, DEFAULT_JOBS_QUERY_PARAMS))
    yield (fetchUserSaga as any)()
    // yield redirectSaga()
  }
}

function* logOutSaga(action) {
  yield call(auth.logOut)
  // yield call(window.location.reload())
}

function* fetchUserSaga(action?: any) {
  const { response, error } = yield call(auth.currentUser)
  if (error) {
    yield put(requestUserFailed(error))
  } else {
    yield put(requestUserSuccess(response))
    yield call(setUserContext, response)
  }
}

function* changeUserLanguageSaga(action) {
  const language = action.payload
  const { error } = yield call(auth.changeActiveLanguage, { language })
  if (error) yield put(errorAction(error))
}

const refreshBrowser = () => {
  // quick and dirty. Strip queryparameters and any numbers from the path
  // that means /model/projects/1111/2/?limit=24 -> /models/projects/
  const next = `/projects?${queryString.stringify(DEFAULT_JOBS_QUERY_PARAMS)}`
  // full refresh of browser with new path
  window.location.replace(next)
}

function* changeUserAccountSaga(action) {
  const account_id = action.payload
  const { error } = yield call(auth.changeActiveAccount, { account_id })
  if (error) {
    yield put(errorAction(error))
  } else {
    // full refresh of app on success
    yield call(refreshBrowser)
  }
}

function* resetPasswordSaga(action) {
  const { error } = yield call(auth.passwordResetRequest, action.payload)
  if (error) yield put(resetPasswordFailed(error))
  else {
    yield put(toPasswordReset('done'))
  }
}

function* changePasswordSaga(action) {
  const { error } = yield call(auth.passwordResetConfirm, action.payload)
  if (error) yield put(resetPasswordConfirmFailed(error))
  else {
    yield put(toPasswordChangeDone())
  }
}

function* registerAccountSaga(action) {
  const language = yield select(getLanguage)
  const { response, error } = yield call(auth.registerAccount, {
    language,
    ...action.payload,
  })
  if (error) {
    yield put(registerAccountFailed(error))
    yield put(
      initSnacksQueue({ message: stringify(error, null, 2), type: 'warning' }),
    )
  } else {
    // TODO: rethink this approach with calling it directly, probably it's possible somehow to improve it
    yield (fetchUserSaga as any)()
    yield put(toProjects(null, null, DEFAULT_JOBS_QUERY_PARAMS))
    yield put(
      initSnacksQueue({
        message: t`Your account was successfully registered.\nWelcome to ebber!`,
        type: 'success',
      }),
    )
    yield put(registerAccountSuccess(response))
  }
}
